import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { StarAuthShell } from "@/pages/star/StarLayout";

export default function StudentLogin() {
  const { studentLogin, user } = useAuth();
  const navigate = useNavigate();
  const [rollNumber, setRollNumber] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && user.role === "student") {
    if (user.token_scope === "onboarding") navigate("/student/onboarding", { replace: true });
    else navigate("/student", { replace: true });
  } }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await studentLogin(rollNumber, password);
      if (u.access === "onboarding") {
        toast.success("Welcome! Please complete your onboarding.");
        navigate("/student/onboarding", { replace: true });
      } else {
        toast.success("Welcome back!");
        navigate("/student", { replace: true });
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <StarAuthShell title="Student Login" subtitle="View your stars and activity.">
      <Seo title="Student Login" />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="student-login-form">
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Roll Number / Student ID</Label>
          <Input value={rollNumber} onChange={(e) => setRollNumber(e.target.value)} placeholder="e.g. 1001" data-testid="student-login-roll" required />
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="student-login-password" required />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="student-login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
      <p className="mt-4 text-sm text-center">
        <Link to="/student/forgot-password" className="font-semibold text-blue-900 hover:underline" data-testid="student-forgot-link">Forgot password?</Link>
      </p>
      <p className="mt-3 text-sm text-slate-700 text-center">
        Existing PTG student? <Link to="/student/activate" className="font-semibold text-blue-900 hover:underline" data-testid="student-activate-link">Activate your account</Link>
      </p>
      <p className="mt-2 text-sm text-slate-500 text-center">
        New admission? <Link to="/student/register" className="font-semibold text-blue-900 hover:underline" data-testid="student-register-link">Create an account</Link>
      </p>
      <p className="mt-2 text-sm text-slate-500 text-center">
        Are you a teacher? <Link to="/teacher/login" className="font-semibold text-blue-900 hover:underline">Teacher login</Link>
      </p>
    </StarAuthShell>
  );
}
